import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { naira, formatDate, statusBadgeClass, statusLabel } from "../../lib/format";
import { KPI, Card, Loading } from "../../components/UI";
import { FileSignature, Tag, FileText, Archive } from "lucide-react";

export default function LegalDashboard() {
  const [leases, setLeases] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/leases"), api.get("/pricing")])
      .then(([l, p]) => { setLeases(l.data); setPricing(p.data); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading/>;
  const pendingLegal = leases.filter(l => l.lease_status === "manager_signed");
  const signed = leases.filter(l => ["completed","lawyer_signed"].includes(l.lease_status));

  return (
    <div className="space-y-6">
      <div className="fade-up">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C9A227]">LEGAL OFFICER</div>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A2E24]">Welcome, Barr. Adekunle</h1>
        <p className="text-[#5E7166] mt-1 text-sm">Review and sign tenancy agreements for Modav Homes.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI label="Pending My Signature" value={pendingLegal.length} icon={FileSignature} testid="kpi-pending-legal"/>
        <KPI label="Signed Leases" value={signed.length} icon={Archive} testid="kpi-signed-leases"/>
        <KPI label="Active Pricing Versions" value={pricing.length} icon={Tag} testid="kpi-pricing-versions"/>
        <KPI label="Total Leases" value={leases.length} icon={FileText} testid="kpi-total-leases"/>
      </div>

      <Card title="Leases Pending Legal Review" testid="legal-pending">
        {pendingLegal.length === 0 ? <div className="text-sm text-[#5E7166] py-6 text-center">No leases awaiting your review.</div> :
          <ul className="divide-y divide-[#E5EBE8]">
            {pendingLegal.map(l => (
              <li key={l.id} className="py-3 flex items-center justify-between text-sm">
                <div>
                  <div className="font-semibold">{l.tenant?.full_name || "—"}</div>
                  <div className="text-xs text-[#5E7166]">{l.unit?.unit_name} ({l.unit_type_name}) • {formatDate(l.lease_start_date)} → {formatDate(l.lease_end_date)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-serif font-bold text-[#0E5C3A]">{naira(l.total_payable)}</span>
                  <Link to={`/leases/${l.id}`} className="btn-gold text-xs">Review & Sign</Link>
                </div>
              </li>
            ))}
          </ul>}
      </Card>

      <Card title="Active Pricing Snapshot" testid="pricing-snapshot" action={<Link to="/pricing" className="text-xs text-[#0E5C3A] hover:text-[#C9A227]">Manage →</Link>}>
        <div className="overflow-x-auto">
          <table className="modav-table">
            <thead><tr><th>Unit Type</th><th>Rent</th><th>Caution</th><th>Agreement</th><th>Service</th><th>Commission</th></tr></thead>
            <tbody>
              {pricing.map(p => (
                <tr key={p.id}>
                  <td className="font-semibold">{p.unit_type_name}</td>
                  <td>{naira(p.rent_amount)}</td>
                  <td>{naira(p.caution_fee)}</td>
                  <td>{naira(p.agreement_fee)}</td>
                  <td>{naira(p.service_charge)}</td>
                  <td>{naira(p.commission_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
